import type { components } from "./generated/vision-api";

export type Problem = components["schemas"]["Problem"];

/** A contract error: a 4xx or 5xx answer, with its problem document when the API sent one (RFC 9457). */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | undefined;
  /** From Retry-After on a 429 or 503 (seconds or an HTTP date), rounded up to whole minutes. */
  readonly retryAfterMinutes: number | undefined;

  constructor(status: number, problem: Problem | undefined, retryAfter?: string | null) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    this.retryAfterMinutes = parseRetryAfter(retryAfter);
  }

  get code(): string | undefined {
    return this.problem?.code;
  }
}

/** The API could not be reached at all (offline, DNS, connection refused, CORS). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("network unreachable", { cause });
    this.name = "NetworkError";
  }
}

export function isProblem(body: unknown): body is Problem {
  if (typeof body !== "object" || body === null) return false;
  const b = body as Record<string, unknown>;
  return typeof b.title === "string" && typeof b.status === "number" && typeof b.code === "string";
}

export function parseRetryAfter(value: string | null | undefined, now: number = Date.now()): number | undefined {
  if (!value) return undefined;
  const seconds = /^\d+$/.test(value.trim()) ? Number(value) : (Date.parse(value) - now) / 1000;
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds / 60) : undefined;
}

/** Turns an error body into an ApiError, keeping the problem document only when it is one. */
export function toApiError(status: number, body: unknown, retryAfter?: string | null): ApiError {
  return new ApiError(status, isProblem(body) ? body : undefined, retryAfter);
}
